/**
 * Visit cadence (PD-16083) — how often a rep should see an account.
 *
 * A client may carry its own cadence; when it carries none the tenant
 * default for its kind applies (monthly for customers, none for leads out
 * of the box). Coverage compares the last visit with the effective cadence:
 *
 *   - `ok`      visited inside the window
 *   - `due`     inside the last quarter of the window (owner decision 2026-09-14)
 *   - `overdue` past the window — or never visited while a cadence applies
 *   - `none`    no cadence applies, so nothing is expected
 *
 * Shared by the planning API (need ordering, coverage on candidates), the
 * map pins (ring color) and the inline cadence selector.
 */

export const VISIT_CADENCES = [
	'weekly',
	'biweekly',
	'monthly',
	'quarterly',
	'none',
] as const;

export type VisitCadence = (typeof VISIT_CADENCES)[number];

export type VisitCoverage = 'ok' | 'due' | 'overdue' | 'none';

export const VISIT_CADENCE_LABELS: Readonly<Record<VisitCadence, string>> = {
	weekly: 'Weekly',
	biweekly: 'Every two weeks',
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	none: 'No cadence',
};

/**
 * PD-16147 — the same cadences said in one word, for dense rows.
 *
 * The full labels above are the ones to read in a settings form, where the
 * phrase is the whole point. In a table row the cadence is a value being
 * scanned down a column, and there the longest of those phrases decided how
 * wide the column had to be: a control showing "Every two weeks" held the
 * candidate accounts table open by about seventy pixels it did not otherwise
 * need, which was enough to keep the table off a common laptop width.
 *
 * Each of these is distinct from the others at its first word rather than its
 * last, so a glance tells them apart and nothing is distinguished only by a
 * tail that a narrow column could clip.
 */
export const VISIT_CADENCE_SHORT_LABELS: Readonly<
	Record<VisitCadence, string>
> = {
	weekly: 'Weekly',
	biweekly: 'Biweekly',
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	none: 'None',
};

/** Window length in days; null when nothing is expected. */
export function visitCadenceDays(cadence: VisitCadence | null): number | null {
	switch (cadence) {
		case 'weekly':
			return 7;
		case 'biweekly':
			return 14;
		case 'monthly':
			return 30;
		case 'quarterly':
			return 91;
		default:
			return null;
	}
}

export function isVisitCadence(value: unknown): value is VisitCadence {
	return (
		typeof value === 'string' &&
		(VISIT_CADENCES as ReadonlyArray<string>).includes(value)
	);
}

export interface VisitCadenceDefaultsModel {
	customer: VisitCadence;
	lead: VisitCadence;
}

export const VISIT_CADENCE_DEFAULTS: Readonly<VisitCadenceDefaultsModel> = {
	customer: 'monthly',
	lead: 'none',
};

/**
 * The cadence that applies to an account: its own when set, otherwise the
 * tenant default for its kind. `leadOrCustomer` is the client column value
 * ('Lead' / 'Customer'); anything that is not a lead counts as a customer.
 */
export function effectiveVisitCadence(
	own: VisitCadence | null | undefined,
	leadOrCustomer: string | null | undefined,
	defaults: VisitCadenceDefaultsModel = VISIT_CADENCE_DEFAULTS,
): VisitCadence {
	if (own != null) {
		return own;
	}
	return (leadOrCustomer ?? '').toLowerCase() === 'lead'
		? defaults.lead
		: defaults.customer;
}

/**
 * Coverage against a cadence. `now` is injectable for tests. Dates in the
 * future count as visited now (a mis-typed future visit should not read as
 * overdue).
 */
export function visitCoverage(
	lastVisit: Date | string | null | undefined,
	cadence: VisitCadence | null,
	now: Date = new Date(),
): VisitCoverage {
	const windowDays = visitCadenceDays(cadence);
	if (windowDays == null) {
		return 'none';
	}
	if (lastVisit == null) {
		return 'overdue';
	}
	const last = lastVisit instanceof Date ? lastVisit : new Date(lastVisit);
	if (Number.isNaN(last.getTime())) {
		return 'overdue';
	}
	const ageDays = Math.max(
		0,
		(now.getTime() - last.getTime()) / (24 * 60 * 60 * 1000),
	);
	if (ageDays > windowDays) {
		return 'overdue';
	}
	// The last quarter of the window is "due": three weeks into a month,
	// ~68 days into a quarter.
	if (ageDays >= windowDays * 0.75) {
		return 'due';
	}
	return 'ok';
}

/**
 * Days past the cadence window (positive = overdue), or null when nothing is
 * expected. Never visited counts as one day past the window: it is overdue,
 * but an account with a real visit long ago still ranks ahead of it (a new
 * account is not the biggest gap on the list).
 */
export function visitOverdueDays(
	lastVisit: Date | string | null | undefined,
	cadence: VisitCadence | null,
	now: Date = new Date(),
): number | null {
	const windowDays = visitCadenceDays(cadence);
	if (windowDays == null) {
		return null;
	}
	if (lastVisit == null) {
		return windowDays + 1;
	}
	const last = lastVisit instanceof Date ? lastVisit : new Date(lastVisit);
	if (Number.isNaN(last.getTime())) {
		return windowDays + 1;
	}
	const ageDays = (now.getTime() - last.getTime()) / (24 * 60 * 60 * 1000);
	return ageDays - windowDays;
}
